import { useEffect, useRef, useState } from 'react';
import { api, type NarrationVoice } from '../../api';
import {
  CATEGORY_LABELS,
  type PlayerPreferences,
  type SoundtrackCategory,
  type SoundtrackLibrary,
} from '../types';
import { savedVoice } from '../voices';

const ORDER: SoundtrackCategory[] = ['EMOTIONAL', 'NOSTALGIC', 'PEACEFUL', 'ADVENTURE', 'CELEBRATION', 'UNCATEGORIZED'];

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; hint?: string }>;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="mb-5" disabled={disabled}>
      <legend className="mb-2 text-xs text-[#f3ece2]/55">{label}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <label
            key={o.value}
            title={o.hint}
            className={`cursor-pointer rounded-full px-3 py-1.5 text-xs transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent ${
              value === o.value
                ? 'bg-[#f3ece2] font-semibold text-[#0b0907]'
                : 'bg-[#f3ece2]/10 text-[#f3ece2]/75 hover:bg-[#f3ece2]/15'
            } ${disabled ? 'opacity-40' : ''}`}
          >
            <input
              type="radio"
              className="sr-only"
              value={o.value}
              aria-label={`${label}: ${o.label}`}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              name={label}
            />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * One narrator dropdown, for the audio language being played: the four Urdu
 * narrators for Urdu, the account's other voices for English.
 */
function NarrationVoice({
  lang,
  voice,
  onChange,
}: {
  lang: 'en' | 'ur';
  voice: string | null;
  onChange: (v: string | null) => void;
}) {
  const [state, setState] = useState<{
    enabled: boolean;
    voices: NarrationVoice[];
    urduVoices: Array<{ id: string; name: string; gender: string }>;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .narrationVoices()
      .then((r) => {
        if (!cancelled) setState({ enabled: r.enabled, voices: r.voices, urduVoices: r.urduVoices });
      })
      .catch(() => {
        if (!cancelled) setState({ enabled: false, voices: [], urduVoices: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  if (!state) return null;
  if (!state.enabled) {
    return (
      <p className="mb-5 text-xs text-[#f3ece2]/50">
        Voice narration needs ELEVENLABS_API_KEY in the server’s .env file.
      </p>
    );
  }
  const urduIds = new Set(state.urduVoices.map((v) => v.id));
  const options =
    lang === 'ur'
      ? state.urduVoices.map((v) => ({ id: v.id, gender: v.gender, label: v.name }))
      : state.voices
          .filter((v) => !urduIds.has(v.id))
          .map((v) => ({
            id: v.id,
            gender: v.gender,
            label: `${v.name.split(' - ')[0]}${v.name.includes(' - ') ? ` · ${v.name.split(' - ')[1]}` : ''}`,
          }));
  const groups = [
    { label: 'Female voices', list: options.filter((o) => o.gender === 'female') },
    { label: 'Male voices', list: options.filter((o) => o.gender === 'male') },
    { label: 'Other voices', list: options.filter((o) => o.gender !== 'female' && o.gender !== 'male') },
  ].filter((g) => g.list.length > 0);
  return (
    <label className="mb-5 block">
      <span className="mb-2 block text-xs text-[#f3ece2]/55">{lang === 'ur' ? 'Urdu narrator' : 'English narrator'}</span>
      <select
        value={voice ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="min-h-11 w-full rounded-md bg-white/[0.07] px-3 py-2 text-sm text-[#f3ece2] ring-1 ring-white/10 [color-scheme:dark]"
      >
        <option value="" className="bg-[#16161c]">
          Automatic (each story’s own voice: {lang === 'ur' ? 'Zafar or Sana' : 'George or Jessica'})
        </option>
        {groups.map((g) => (
          <optgroup key={g.label} label={g.label} className="bg-[#16161c]">
            {g.list.map((v) => (
              <option key={v.id} value={v.id} className="bg-[#16161c]">
                {v.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`mb-3 flex items-start justify-between gap-4 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
      <span>
        <span className="block text-sm text-[#f3ece2]/85">{label}</span>
        {hint && <span className="block text-xs text-[#f3ece2]/40">{hint}</span>}
      </span>
      <input
        type="checkbox"
        role="switch"
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="focus-ring mt-0.5 h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-[#f3ece2]/20 transition before:block before:h-4 before:w-4 before:translate-x-0.5 before:rounded-full before:bg-[#f3ece2] before:transition checked:bg-[#f3ece2]/70 checked:before:translate-x-[18px] checked:before:bg-[#0b0907]"
      />
    </label>
  );
}

function Slider({ label, value, onChange, disabled }: { label: string; value: number; onChange: (v: number) => void; disabled?: boolean }) {
  return (
    <label className={`mb-4 block ${disabled ? 'opacity-40' : ''}`}>
      <span className="mb-1.5 flex justify-between text-xs text-[#f3ece2]/55">
        <span>{label}</span>
        <span className="tabular-nums">{Math.round(value * 100)}%</span>
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        className="w-full accent-[#f3ece2]"
      />
    </label>
  );
}

function fmtDuration(s: number | null) {
  if (!s) return '';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, '0')}`;
}

/** The user's own music: pick what plays, add files, sort into moods. */
function MusicLibrary({
  library,
  prefs,
  update,
  onLibraryChange,
}: {
  library: SoundtrackLibrary | null;
  prefs: PlayerPreferences;
  update: (p: Partial<PlayerPreferences>) => void;
  onLibraryChange: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<SoundtrackCategory>('UNCATEGORIZED');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const tracks = library?.tracks ?? [];
  const inPlaylist = new Set(prefs.playlist);

  const add = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setMessages([]);
    try {
      const res = await api.addSoundtracks(Array.from(files), category);
      setMessages(
        res.results.map((r) =>
          r.status === 'added'
            ? `Added “${r.track?.title}”`
            : r.status === 'duplicate'
              ? `“${r.track?.title}” is already in your music`
              : `${r.filename}: ${r.error}`,
        ),
      );
      onLibraryChange();
    } catch (err) {
      setMessages([(err as Error).message]);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const toggle = (id: string) =>
    update({ playlist: inPlaylist.has(id) ? prefs.playlist.filter((x) => x !== id) : [...prefs.playlist, id] });

  const remove = async (id: string) => {
    await api.deleteSoundtrack(id).catch((err) => setMessages([(err as Error).message]));
    update({ playlist: prefs.playlist.filter((x) => x !== id) });
    onLibraryChange();
  };

  const groups = ORDER.map((c) => ({ c, list: tracks.filter((t) => t.category === c) })).filter((g) => g.list.length > 0);

  return (
    <div>
      {tracks.length === 0 ? (
        <div className="mb-4 rounded-lg bg-[#f3ece2]/5 p-4 text-sm text-[#f3ece2]/65 ring-1 ring-[#f3ece2]/10">
          <p className="mb-1 font-medium text-[#f3ece2]/90">No music yet</p>
          <p className="text-xs leading-relaxed text-[#f3ece2]/50">
            Add songs you own (MP3, M4A, AAC, OGG, WAV or FLAC) and they’ll play softly under your stories, dipping
            whenever a video has its own sound.
          </p>
        </div>
      ) : (
        <div className="mb-4 space-y-4">
          {groups.map(({ c, list }) => (
            <div key={c}>
              <p className="mb-1.5 text-xs text-[#f3ece2]/45">{CATEGORY_LABELS[c]}</p>
              <ul className="space-y-1">
                {list.map((t) => (
                  <li key={t.id} className="group flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-[#f3ece2]/5">
                    <input
                      type="checkbox"
                      checked={inPlaylist.has(t.id)}
                      onChange={() => toggle(t.id)}
                      aria-label={`Play “${t.title}” in stories`}
                      className="focus-ring h-4 w-4 shrink-0 accent-[#f3ece2]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-[#f3ece2]/85">{t.title}</span>
                      <span className="block truncate text-[11px] text-[#f3ece2]/40">
                        {[t.artist, fmtDuration(t.durationSeconds)].filter(Boolean).join(', ')}
                      </span>
                    </span>
                    {t.builtIn ? (
                      <span className="shrink-0 text-[11px] text-[#f3ece2]/35">Built-in</span>
                    ) : (
                    <>
                    <select
                      value={t.category}
                      onChange={async (e) => {
                        await api.updateSoundtrack(t.id, { category: e.target.value as SoundtrackCategory });
                        onLibraryChange();
                      }}
                      aria-label={`Mood for “${t.title}”`}
                      className="focus-ring max-w-[7.5rem] rounded bg-[#0b0907] px-1.5 py-1 text-[11px] text-[#f3ece2]/70 ring-1 ring-[#f3ece2]/15"
                    >
                      {ORDER.map((c2) => (
                        <option key={c2} value={c2}>
                          {CATEGORY_LABELS[c2]}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => void remove(t.id)}
                      aria-label={`Remove “${t.title}” from your music`}
                      className="focus-ring rounded p-1 text-[#f3ece2]/35 hover:text-red-300"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {prefs.playlist.length === 0 && (
            <p className="text-xs text-[#f3ece2]/50">
              Nothing ticked: each story plays built-in music that fits its mood. Tick songs to choose your own.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => input.current?.click()}
          disabled={busy}
          className="focus-ring rounded-full bg-[#f3ece2]/10 px-4 py-2 text-xs font-semibold text-[#f3ece2] ring-1 ring-[#f3ece2]/15 transition hover:bg-[#f3ece2]/20 disabled:opacity-40"
        >
          {busy ? 'Adding…' : 'Add music'}
        </button>
        <label className="flex items-center gap-2 text-xs text-[#f3ece2]/50">
          as
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as SoundtrackCategory)}
            className="focus-ring rounded bg-[#0b0907] px-1.5 py-1 text-xs text-[#f3ece2]/75 ring-1 ring-[#f3ece2]/15"
          >
            {ORDER.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
        <input
          ref={input}
          type="file"
          multiple
          accept={library?.limits.extensions.join(',') ?? 'audio/*'}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => void add(e.target.files)}
        />
      </div>
      {messages.length > 0 && (
        <ul className="mt-3 space-y-0.5 text-xs text-[#f3ece2]/60" role="status">
          {messages.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Player settings: a side sheet on wide screens, a bottom sheet on phones.
 * Every change applies immediately and is saved to the account.
 */
export function SettingsPanel({
  prefs,
  update,
  library,
  onLibraryChange,
  hasDepth,
  onClose,
  audioLang,
  subtitleLang,
  onAudio,
  onSubtitles,
}: {
  prefs: PlayerPreferences;
  update: (p: Partial<PlayerPreferences>) => void;
  /** Narration and subtitle languages playing now (null = off). */
  audioLang: 'en' | 'ur' | null;
  subtitleLang: 'en' | 'ur' | null;
  onAudio: (v: 'off' | 'en' | 'ur') => void;
  onSubtitles: (v: 'off' | 'en' | 'ur') => void;
  library: SoundtrackLibrary | null;
  onLibraryChange: () => void;
  /** This story has depth maps, so the 3D option can work. */
  hasDepth: boolean;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panel.current?.querySelector<HTMLElement>('button, input')?.focus();
  }, []);
  const still = prefs.photoAnimation === 'still';

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label="Player settings"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
      className="absolute inset-x-0 bottom-0 z-50 max-h-[78vh] overflow-y-auto rounded-t-2xl bg-[#17140f]/95 p-5 text-[#f3ece2] shadow-2xl ring-1 ring-[#f3ece2]/10 backdrop-blur-xl sm:inset-x-auto sm:top-0 sm:right-0 sm:bottom-0 sm:max-h-none sm:w-[380px] sm:rounded-none sm:p-6"
    >
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-base font-semibold">Player settings</h2>
        <button onClick={onClose} className="focus-ring rounded-full p-1.5 text-[#f3ece2]/60 hover:text-[#f3ece2]" aria-label="Close settings">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <h3 className="mb-3 text-sm font-semibold text-[#f3ece2]/90">Playback</h3>
      <Segmented
        label="Pace"
        value={prefs.pace}
        onChange={(pace) => update({ pace })}
        options={[
          { value: 'relaxed', label: 'Relaxed', hint: 'About 6–8 seconds per photo' },
          { value: 'cinematic', label: 'Cinematic', hint: 'About 4–5 seconds per photo' },
          { value: 'dynamic', label: 'Dynamic', hint: 'About 3 seconds per photo' },
        ]}
      />
      <Segmented
        label="Transitions"
        value={prefs.transition}
        onChange={(transition) => update({ transition })}
        options={[
          { value: 'auto', label: 'Automatic', hint: 'Dissolves, with a fade through black between scenes' },
          { value: 'dissolve', label: 'Dissolve' },
          { value: 'fade-black', label: 'Fade to black' },
          { value: 'push', label: 'Push' },
          { value: 'zoom', label: 'Zoom' },
          { value: 'blur', label: 'Soft blur' },
          { value: 'cut', label: 'Cut' },
        ]}
      />
      <Segmented
        label="Animate photos"
        value={prefs.photoAnimation === 'ask' ? (hasDepth ? 'depth' : 'motion') : prefs.photoAnimation}
        onChange={(photoAnimation) => update({ photoAnimation, ...(photoAnimation !== 'still' && prefs.motion === 'off' ? { motion: 'standard' } : {}) })}
        options={[
          { value: 'still', label: 'Still', hint: 'Photos stay still, with soft dissolves' },
          { value: 'motion', label: 'Gentle motion', hint: 'A slow zoom and pan' },
          {
            value: 'depth',
            label: '3D depth',
            hint: hasDepth ? 'A slow 3D camera move through each photo' : 'Still being prepared for these photos; gentle motion until then',
          },
        ]}
      />
      <Segmented
        label={still ? 'Motion strength (photos are still)' : 'Motion strength'}
        value={prefs.motion === 'off' ? 'standard' : prefs.motion}
        onChange={(motion) => update({ motion })}
        disabled={still}
        options={[
          { value: 'subtle', label: 'Subtle' },
          { value: 'standard', label: 'Standard' },
          { value: 'expressive', label: 'Expressive' },
        ]}
      />

      <h3 className="mt-7 mb-3 text-sm font-semibold text-[#f3ece2]/90">Look</h3>
      <Segmented
        label="Color"
        value={prefs.colorTreatment}
        onChange={(colorTreatment) => update({ colorTreatment })}
        options={[
          { value: 'natural', label: 'Natural' },
          { value: 'warm', label: 'Warm' },
          { value: 'film', label: 'Film' },
        ]}
      />
      <Toggle label="Vignette" checked={prefs.vignette} onChange={(vignette) => update({ vignette })} />

      <h3 className="mt-7 mb-3 text-sm font-semibold text-[#f3ece2]/90">Story</h3>
      <Toggle label="Opening title" checked={prefs.openingCard} onChange={(openingCard) => update({ openingCard })} />
      <Toggle label="Ending title" checked={prefs.endingCard} onChange={(endingCard) => update({ endingCard })} />
      <Toggle
        label="Date and place"
        hint="Shown only when the photo or video recorded them."
        checked={prefs.overlays === 'minimal'}
        onChange={(on) => update({ overlays: on ? 'minimal' : 'off' })}
      />
      <Segmented
        label="Audio (story narration)"
        value={audioLang ?? 'off'}
        onChange={onAudio}
        options={[
          { value: 'off', label: 'Off' },
          { value: 'en', label: 'English' },
          { value: 'ur', label: 'Urdu' },
        ]}
      />
      {audioLang && (
        <NarrationVoice
          key={audioLang}
          lang={audioLang}
          voice={audioLang === 'ur' ? savedVoice(prefs, 'ur') : savedVoice(prefs, 'en')}
          onChange={(v) => update(audioLang === 'ur' ? { narrationVoiceUr: v } : { narrationVoice: v })}
        />
      )}
      <Segmented
        label="Subtitles (the story’s lines, under each memory)"
        value={subtitleLang ?? 'off'}
        onChange={onSubtitles}
        options={[
          { value: 'off', label: 'Off' },
          { value: 'en', label: 'English' },
          { value: 'ur', label: 'Urdu' },
        ]}
      />

      <h3 className="mt-7 mb-3 text-sm font-semibold text-[#f3ece2]/90">Sound</h3>
      <Toggle label="Background music" checked={prefs.musicEnabled} onChange={(musicEnabled) => update({ musicEnabled })} />
      <Slider label="Music volume" value={prefs.musicVolume} disabled={!prefs.musicEnabled} onChange={(musicVolume) => update({ musicVolume })} />
      <Slider label="Video sound" value={prefs.videoVolume} onChange={(videoVolume) => update({ videoVolume })} />
      <MusicLibrary library={library} prefs={prefs} update={update} onLibraryChange={onLibraryChange} />
    </div>
  );
}
